/**
 * caller-check — «это правда он звонит?».
 *
 * ПОРЯДОК ЭКРАНОВ ЗДЕСЬ — САМ ОТВЕТ, А НЕ ОФОРМЛЕНИЕ.
 *
 * У инструмента два разных посетителя, и до переделки они были свалены в одну
 * простыню из пяти разделов. Первый пришёл ЗАРАНЕЕ: он спокоен, хочет завести
 * кодовое слово и записать голоса. Второй пришёл В МОМЕНТ ЗВОНКА: у него на
 * линии «сын в беде», руки трясутся, и ему нужен ответ за десять секунд.
 * Второму нельзя показывать «сгенерируйте кодовое слово» — сейчас оно
 * бесполезно, о нём не договорились. Первому нельзя показывать красную
 * тревожную панель — он ни в какой опасности не находится.
 *
 * Поэтому здесь две вкладки, и «мне звонят» открыта по умолчанию: цена ошибки
 * несимметрична. Человек, пришедший готовиться, потеряет одно нажатие;
 * человек на линии с мошенником, которому пришлось прокрутить страницу мимо
 * генератора слов, теряет деньги.
 *
 * Наверху обеих вкладок — правило перезвона. Оно единственное работает всегда
 * и не зависит ни от какой техники, поэтому стоит ВЫШЕ прибора, а не после
 * него.
 *
 * Прибор отвечает НЕСИММЕТРИЧНО: «это не он» он сказать может, «это точно он»
 * не может никто. Инструмент, который меняет местами эти две вещи, продаёт
 * уверенность вместо защиты.
 */
import * as store from './store.js';
import * as cw from './codeword.js';
import { createListener } from './listen.js';
import { createRedFlags } from './redflags.js';

const T = window.__CC_T || {};
const CFG = window.__CC_CFG || {};
const SLUG = 'caller-check';

const $ = (id) => document.getElementById(id);
const on = (el, ev, fn) => { if (el) el.addEventListener(ev, fn); };
const show = (el, yes) => { if (el) el.hidden = !yes; };

function ae(type, data) {
    try { if (window.__ae) window.__ae(type, Object.assign({ slug: SLUG }, data || {})); } catch (e) {}
}
function toast(m, k) { try { if (window.__toast) window.__toast(m, k || 'info'); } catch (e) {} }
function t(k, vars) {
    let s = T[k] || '';
    if (vars) Object.keys(vars).forEach((n) => { s = s.split(':' + n).join(vars[n]); });
    return s;
}
function setStatus(el, text, kind) {
    if (!el) return;
    el.textContent = text || '';
    el.className = 'cc-status' + (kind ? ' is-' + kind : '');
}
/** Телефон в ссылку tel: — только цифры и ведущий плюс. */
function telHref(raw) {
    const s = String(raw || '').replace(/[^\d+]/g, '');
    return s.length >= 5 ? 'tel:' + s : null;
}

let listener = null;
let flags = null;
let lastLiveVerdict = null;

/* ══ вкладки ══════════════════════════════════════════════════════════ */

function setTab(name) {
    const now = name !== 'prep';
    $('ccTabNow').setAttribute('aria-selected', String(now));
    $('ccTabPrep').setAttribute('aria-selected', String(!now));
    show($('ccPaneNow'), now);
    show($('ccPanePrep'), !now);
    store.save({ tab: now ? 'now' : 'prep' });
    ae('feature_use', { f: 'cc_tab', tab: now ? 'now' : 'prep' });
}

/* ══ правило перезвона: кому звонить ══════════════════════════════════ */

/**
 * Кнопка «перезвонить» имеет смысл только с номером, который человек записал
 * САМ и заранее. Номер, с которого звонят, здесь не участвует вообще — в этом
 * весь смысл правила: набрать надо СВОЙ номер, а не тот, что определился.
 */
function renderCallback() {
    const box = $('ccCallback');
    if (!box) return;
    box.textContent = '';
    const withPhone = store.get().people.filter((p) => telHref(p.phone));
    if (!withPhone.length) {
        const a = document.createElement('button');
        a.type = 'button';
        a.className = 'cc-chip cc-chip-ghost';
        a.textContent = t('callback_add');
        a.addEventListener('click', () => { setTab('prep'); focusPrep(); });
        box.appendChild(a);
        return;
    }
    withPhone.forEach((p) => {
        const a = document.createElement('a');
        a.className = 'cc-chip cc-chip-call';
        a.href = telHref(p.phone);
        a.rel = 'nofollow';
        a.textContent = '📞 ' + p.name;
        a.addEventListener('click', () => ae('feature_use', { f: 'cc_callback_dial' }));
        box.appendChild(a);
    });
}

function focusPrep() {
    const el = $('ccName');
    if (el) { el.focus({ preventScroll: true }); el.scrollIntoView({ block: 'nearest' }); }
}

/* ══ кодовое слово ════════════════════════════════════════════════════ */

let wordTimer = 0;

function paintStaticWord() {
    const s = store.get();
    const el = $('ccWordStatic');
    if (!el) return;
    el.textContent = s.word || t('word_placeholder');
    el.classList.toggle('is-empty', !s.word);
    const card = $('ccCardWord');
    if (card) card.textContent = s.word || '—';
    show($('ccCard'), !!s.word);
}

async function paintNowWord() {
    const s = store.get();
    const box = $('ccWordNow');
    const meta = $('ccWordNowMeta');
    if (!box) return;

    if (s.mode === 'rotating' && s.circle) {
        const step = cw.stepNow(s.circle);
        const [nowWord, prevWord] = await Promise.all([
            cw.wordAt(s.circle, step), cw.wordAt(s.circle, step - 1),
        ]);
        box.textContent = nowWord || '—';
        box.classList.remove('is-empty');
        const left = cw.secondsLeft(s.circle);
        meta.textContent = t('word_rotates_in', { n: Math.ceil(left / 60) }) + ' · ' + t('word_prev', { w: prevWord || '—' });
        show($('ccWordNowRow'), true);
        show($('ccWordNowNone'), false);
        return;
    }
    if (s.word) {
        box.textContent = s.word;
        box.classList.remove('is-empty');
        meta.textContent = t('word_static_hint');
        show($('ccWordNowRow'), true);
        show($('ccWordNowNone'), false);
        return;
    }
    show($('ccWordNowRow'), false);
    show($('ccWordNowNone'), true);
}

async function paintCircle() {
    const s = store.get();
    const has = !!s.circle;
    show($('ccCircleBox'), has);
    show($('ccCircleNew'), !has);
    if (!has) return;
    const step = cw.stepNow(s.circle);
    const [nowWord, prevWord] = await Promise.all([
        cw.wordAt(s.circle, step), cw.wordAt(s.circle, step - 1),
    ]);
    $('ccCircleWord').textContent = nowWord || '—';
    const left = cw.secondsLeft(s.circle);
    const mm = Math.floor(left / 60), ss = String(left % 60).padStart(2, '0');
    $('ccCircleTime').textContent = mm + ':' + ss;
    $('ccCircleRing').style.setProperty('--p', String(1 - left / (s.circle.period || cw.PERIOD_SEC)));
    $('ccCirclePrev').textContent = t('word_prev', { w: prevWord || '—' });
    $('ccCircleTitle').textContent = s.circle.name || t('circle_unnamed');
}

function startWordClock() {
    if (wordTimer) clearInterval(wordTimer);
    wordTimer = setInterval(() => {
        const s = store.get();
        if (s.mode === 'rotating' && s.circle) { paintNowWord(); paintCircle(); }
    }, 1000);
}

function renderQr() {
    const s = store.get();
    const box = $('ccQr');
    if (!box || !s.circle) return;
    const link = cw.circleLink(s.circle, CFG.pageUrl);
    const inp = $('ccCircleLink');
    if (inp) inp.value = link;
    if (typeof window.qrcode !== 'function') { box.textContent = ''; return; }
    let qr;
    try {
        qr = window.qrcode(0, 'M');   // 0 = версия подбирается по длине сама
        qr.addData(link);
        qr.make();
    } catch (e) { box.textContent = ''; return; }
    /* SVG строим сами, а не createSvgTag: нужен масштабируемый viewBox без
       фиксированных width/height, иначе код не тянется под ширину карточки. */
    const n = qr.getModuleCount();
    let d = '';
    for (let y = 0; y < n; y++) {
        let run = -1;
        for (let x = 0; x <= n; x++) {
            const dark = x < n && qr.isDark(y, x);
            if (dark && run < 0) run = x;
            if (!dark && run >= 0) { d += 'M' + run + ' ' + y + 'h' + (x - run) + 'v1h' + -(x - run) + 'z'; run = -1; }
        }
    }
    box.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + n + ' ' + n
        + '" shape-rendering="crispEdges" role="img" aria-label="QR"><rect width="' + n + '" height="' + n
        + '" fill="#fff"/><path fill="#000" d="' + d + '"/></svg>';
}

/* ══ люди ═════════════════════════════════════════════════════════════ */

function renderPeople() {
    const box = $('ccPeople');
    if (!box) return;
    box.textContent = '';
    const people = store.get().people;
    show($('ccPeopleEmpty'), !people.length);
    people.forEach((p) => {
        const row = document.createElement('div');
        row.className = 'cc-person';

        const nm = document.createElement('span');
        nm.className = 'cc-person-nm';
        nm.textContent = p.name;

        const mt = document.createElement('span');
        mt.className = 'cc-person-mt';
        mt.textContent = t('enroll_secs', { n: Math.round(p.feat.speechSec) });

        const tel = document.createElement('input');
        tel.type = 'tel';
        tel.className = 'cc-in cc-in-tel';
        tel.value = p.phone || '';
        tel.placeholder = t('phone_ph');
        tel.setAttribute('aria-label', t('phone_aria', { n: p.name }));
        tel.autocomplete = 'tel';
        tel.addEventListener('change', () => {
            store.updatePerson(p.id, { phone: tel.value.trim() });
            renderCallback();
        });

        const x = document.createElement('button');
        x.type = 'button';
        x.className = 'cc-x';
        x.textContent = '×';
        x.setAttribute('aria-label', t('enroll_delete_aria', { n: p.name }));
        x.addEventListener('click', () => {
            if (!window.confirm(t('enroll_confirm_del', { n: p.name }))) return;
            store.removePerson(p.id);
            renderPeople();
            renderCallback();
            paintVoiceReadiness();
            ae('feature_use', { f: 'cc_reference_deleted' });
        });

        row.append(nm, mt, tel, x);
        box.appendChild(row);
    });
}

/* ══ панель ответа ════════════════════════════════════════════════════ */

function paintVerdict(payload) {
    const box = $('ccVerdict');
    const v = payload.verdict;
    show($('ccResult'), true);
    box.className = 'cc-verdict is-' + v;
    $('ccVerdictText').textContent = t('result_' + v);

    let note = t('result_note_' + v);
    if (payload.noReference) note = t('no_reference');
    else if (payload.quality && !payload.quality.ok) note = t('q_' + payload.quality.reason) || t('quality_bad');
    $('ccVerdictNote').innerHTML = note;

    const bits = [];
    if (payload.match) {
        const b = payload.match.best;
        bits.push(t('match_with', { n: b.person.name }));
        bits.push(t('score_label', { n: Math.round(b.cmp.score) }));
        const st = b.cmp.semitones;
        if (st >= 1.5 && payload.result) {
            const refF0 = store.unpackFeat(b.person.feat).f0.median;
            const key = payload.result.feat.f0.median < refF0 ? 'pitch_lower' : 'pitch_higher';
            bits.push(t(key, { n: st.toFixed(1) }));
        } else if (st < 1.5) {
            bits.push(t('pitch_same'));
        }
        /* Два эталона с почти равным счётом — это «не знаю, кто из них», а не
           «вот этот». Молчать об этом значит выдавать случайный порядок
           сортировки за вывод. */
        const r = payload.match.runnerUp;
        if (r && Math.abs(r.cmp.score - b.cmp.score) < 1.5) bits.push(t('match_ambiguous', { n: r.person.name }));
    }
    $('ccVerdictMeta').textContent = bits.filter(Boolean).join(' · ');

    if (payload.result) paintObservations(payload.result);
    show($('ccObsWrap'), !!payload.result);

    /* Телефон у уха — экрана не видно. Единственный канал, который дойдёт,
       это вибрация, и тратить её можно только на СИЛЬНОЕ утверждение. */
    if (v === 'different' && lastLiveVerdict !== 'different') {
        try { if (navigator.vibrate) navigator.vibrate([180, 90, 180, 90, 420]); } catch (e) {}
        /* Пульс — Web Animations (iOS 13.4+). Без них бросок обрывал строку ниже:
           `lastLiveVerdict` не записывался, и вибрация повторялась на каждом
           живом обновлении. */
        if (typeof box.animate === 'function') {
            box.animate(
                [{ transform: 'scale(1)' }, { transform: 'scale(1.02)' }, { transform: 'scale(1)' }],
                { duration: 420, easing: 'ease-out' },
            );
        }
    }
    lastLiveVerdict = v;
}

function paintObservations(r) {
    const list = $('ccObs');
    if (!list) return;
    list.textContent = '';
    window.VoiceVerify.observations(r.feat, r.pcm, r.sr).forEach((o) => {
        const li = document.createElement('li');
        if (o.severity === 'warn') li.className = 'is-warn';
        const val = document.createElement('span');
        val.className = 'cc-obs-v';
        if (o.id === 'bandwidth') val.textContent = (o.value / 1000).toFixed(1) + ' ' + t('khz');
        else if (o.id === 'speech_sec') val.textContent = o.value + ' ' + t('sec');
        else val.textContent = o.value + t('percent');
        const tx = document.createElement('span');
        tx.textContent = t('obs_' + o.id);
        li.append(val, tx);
        list.appendChild(li);
    });
}

/* ══ источник звука ═══════════════════════════════════════════════════ */

let source = 'mic';

function setSource(name) {
    if (listener && listener.running()) listener.stop();
    source = name;
    document.querySelectorAll('#ccSrc [data-src]').forEach((b) => {
        b.setAttribute('aria-pressed', String(b.dataset.src === name));
    });
    show($('ccFileWrap'), name === 'file');
    show($('ccListenWrap'), name !== 'file');
    setStatus($('ccStatus'), name === 'tab' ? t('src_tab_hint') : (name === 'mic' ? t('src_mic_hint') : t('src_file_hint')));
    store.save();
    ae('feature_use', { f: 'cc_source', source: name });
}

function paintVoiceReadiness() {
    const has = store.get().people.length > 0;
    show($('ccNeedRef'), !has);
}

function setListenButton(running) {
    const b = $('ccListen');
    if (!b) return;
    b.textContent = running ? t('btn_listen_stop') : t('btn_listen_start');
    b.classList.toggle('is-live', running);
}

async function toggleListen() {
    if (listener.running()) { listener.stop(); return; }
    if (!store.get().people.length) {
        /* Кнопка не «ничего не делает» и не выключена: она объясняет, чего не
           хватает, и ведёт туда, где это заводится. Немой отказ здесь — это
           человек с мошенником на линии, который решил, что сайт сломан. */
        setStatus($('ccStatus'), t('no_reference'), 'warn');
        show($('ccNeedRef'), true);
        $('ccNeedRef').scrollIntoView({ block: 'nearest' });
        return;
    }
    setStatus($('ccStatus'), t('listen_opening'));
    try {
        await listener.start(source, () => store.get().people, store.unpackFeat);
    } catch (e) {
        const code = (e && (e.code || e.name)) || '';
        /* Причина названа, а не свёрнута в «не удалось»: у захвата экрана свои
           поводы отказать, и человек должен знать, какой именно ему чинить. */
        let msg = t('err_' + code);
        if (!msg && source === 'mic' && window.MicManager) {
            try { msg = new window.MicManager({ locale: window.__LOCALE, slug: SLUG }).getErrorMessage(e); } catch (_) {}
        }
        if (!msg) msg = code === 'NotAllowedError' ? t('live_denied') : (t('listen_failed') || String(e));
        setStatus($('ccStatus'), msg, 'bad');
        ae('tool_error', { stage: 'cc_listen', source, code: String(code) });
    }
}

/* ══ фразы давления ═══════════════════════════════════════════════════ */

/** 'unknown' — не спрашивали (и не спросим до нажатия, см. шапку redflags.js). */
let flagState = 'unknown';

function renderFlagChips(hitIds) {
    const box = $('ccFlags');
    if (!box) return;
    box.textContent = '';
    const rules = CFG.redflags || [];
    rules.forEach((r) => {
        const el = document.createElement('span');
        el.className = 'cc-flag' + (hitIds.indexOf(r.id) !== -1 ? ' is-hit' : '');
        el.textContent = r.label;
        box.appendChild(el);
    });
    show(box, rules.length > 0);
}

const hits = [];

/**
 * НИЧЕГО не спрашивает у браузера: список фраз — это памятка, и она полезна
 * сама по себе. Проба возможностей уходит в обработчик нажатия, потому что
 * сама проба способна убить вкладку (см. шапку redflags.js).
 */
function initFlags() {
    const wrap = $('ccFlagsWrap');
    if (!wrap || !(CFG.redflags || []).length) { show(wrap, false); return; }
    flags = createRedFlags({ slug: SLUG });
    if (flags.poisoned()) flagState = 'unsupported';
    renderFlagChips([]);
    paintFlagState();
}

function paintFlagState() {
    const btn = $('ccFlagsToggle');
    const note = $('ccFlagsNote');
    if (!btn) return;
    if (flagState === 'unsupported') {
        btn.hidden = true;
        note.textContent = t('flags_unsupported');
        return;
    }
    btn.hidden = false;
    if (flags && flags.running()) {
        btn.textContent = t('flags_stop');
        btn.setAttribute('aria-pressed', 'true');
        note.textContent = t('flags_running');
    } else {
        btn.textContent = flagState === 'downloadable' ? t('flags_install') : t('flags_start');
        btn.setAttribute('aria-pressed', 'false');
        note.textContent = flagState === 'downloadable' ? t('flags_install_note') : t('flags_note');
    }
}

async function toggleFlags() {
    if (!flags) return;
    if (flags.running()) { flags.stop(); paintFlagState(); return; }
    if (flagState === 'unknown') {
        const btn = $('ccFlagsToggle');
        if (btn) btn.disabled = true;
        $('ccFlagsNote').textContent = t('flags_probing');
        flagState = await flags.probe(CFG.speechLang || 'en-US');
        if (btn) btn.disabled = false;
        ae('feature_use', { f: 'cc_speech_probe', state: flagState });
        paintFlagState();
        if (flagState !== 'available') return;
    }
    if (flagState === 'downloadable') {
        const note = $('ccFlagsNote');
        note.textContent = t('flags_installing');
        const ok = await flags.install(CFG.speechLang || 'en-US');
        flagState = ok ? 'available' : 'unsupported';
        paintFlagState();
        if (!ok) return;
    }
    hits.length = 0;
    renderFlagChips(hits);
    const started = flags.start(CFG.speechLang || 'en-US', CFG.redflags || [], (id, phrase) => {
        if (hits.indexOf(id) === -1) hits.push(id);
        renderFlagChips(hits);
        const heard = $('ccFlagsHeard');
        if (heard) {
            const li = document.createElement('li');
            li.textContent = '«' + phrase + '»';
            heard.appendChild(li);
            show($('ccFlagsHeardWrap'), true);
        }
        try { if (navigator.vibrate) navigator.vibrate(hits.length >= 2 ? [200, 100, 200] : 140); } catch (e) {}
    }, () => paintFlagState());
    if (!started) { flagState = 'unsupported'; }
    paintFlagState();
}

/* ══ запись эталона ═══════════════════════════════════════════════════ */

const ENROLL_MAX_SEC = 30;
let rec = null;

async function recToggle() {
    if (rec) { recStop(); return; }
    const name = ($('ccName').value || '').trim();
    if (!name) {
        setStatus($('ccRecStatus'), t('enroll_name_needed'), 'warn');
        $('ccName').focus();
        return;
    }
    setStatus($('ccRecStatus'), t('listen_opening'));
    try {
        const mgr = window.MicManager ? new window.MicManager({ locale: window.__LOCALE, slug: SLUG }) : null;
        const stream = mgr
            ? await mgr.startRaw({ audio: { channelCount: 1 } })
            : await window.__tmbGum({ audio: { channelCount: 1 } });
        const mr = new MediaRecorder(stream);
        const chunks = [];
        mr.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
        mr.onstop = () => recFinish(chunks, name);
        mr.start();

        const AC = window.AudioContext || window.webkitAudioContext;
        const c = new AC();
        const an = c.createAnalyser();
        an.fftSize = 512;
        c.createMediaStreamSource(stream).connect(an);

        rec = { mgr, stream, mr, c, an, t0: Date.now(), raf: 0, stop: 0 };
        rec.stop = setTimeout(recStop, ENROLL_MAX_SEC * 1000);
        meterLoop();
        $('ccRec').classList.add('is-live');
        $('ccRec').textContent = t('enroll_stop');
        ae('feature_use', { f: 'cc_enroll_start' });
    } catch (e) {
        let msg = '';
        try { if (window.MicManager) msg = new window.MicManager({ locale: window.__LOCALE, slug: SLUG }).getErrorMessage(e); } catch (_) {}
        setStatus($('ccRecStatus'), msg || t('live_denied'), 'bad');
        ae('tool_error', { stage: 'cc_enroll_mic', code: String((e && (e.code || e.name)) || e) });
    }
}

function meterLoop() {
    if (!rec) return;
    const buf = new Uint8Array(rec.an.fftSize);
    rec.an.getByteTimeDomainData(buf);
    let s = 0;
    for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; s += v * v; }
    const rms = Math.sqrt(s / buf.length);
    setLevel($('ccRecMeter'), Math.min(1, rms * 4));
    const sec = Math.round((Date.now() - rec.t0) / 1000);
    setStatus($('ccRecStatus'), t('enroll_recording', { n: sec, max: ENROLL_MAX_SEC }));
    rec.raf = requestAnimationFrame(meterLoop);
}

function recStop() {
    if (!rec) return;
    const r = rec;
    rec = null;
    if (r.stop) clearTimeout(r.stop);
    if (r.raf) cancelAnimationFrame(r.raf);
    try { if (r.mr.state !== 'inactive') r.mr.stop(); } catch (e) {}
    try { if (r.mgr && r.mgr.stop) r.mgr.stop(); } catch (e) {}
    r.stream.getTracks().forEach((tr) => tr.stop());
    try { r.c.close(); } catch (e) {}
    setLevel($('ccRecMeter'), 0);
    $('ccRec').classList.remove('is-live');
    $('ccRec').textContent = t('enroll_start');
}

async function recFinish(chunks, name) {
    if (!chunks.length) return;
    const blob = new Blob(chunks, { type: chunks[0].type || 'audio/webm' });
    setStatus($('ccRecStatus'), t('analyzing'));
    await enroll(blob, name, $('ccRecStatus'));
}

async function enroll(file, name, statusEl) {
    try {
        const r = await listener.analyseFile(file);
        if (!r.feat || !r.q.ok) {
            setStatus(statusEl, t('q_' + (r.q ? r.q.reason : 'no_speech')) || t('quality_bad'), 'warn');
            return;
        }
        store.addPerson(name, r.feat, ($('ccPhone').value || '').trim());
        $('ccName').value = '';
        $('ccPhone').value = '';
        renderPeople();
        renderCallback();
        paintVoiceReadiness();
        setStatus(statusEl, t('enroll_saved', { n: name }), 'good');
        ae('feature_use', { f: 'cc_reference_added', sec: Math.round(r.feat.speechSec) });
    } catch (e) {
        setStatus(statusEl, t('decode_err'), 'bad');
        ae('tool_error', { stage: 'cc_enroll_decode' });
    }
}

function setLevel(el, v) { if (el) el.style.setProperty('--l', String(Math.max(0, Math.min(1, v)))); }

/* ══ вопросы ══════════════════════════════════════════════════════════ */

let qIdx = 0;
function nextQuestion(step) {
    const pool = CFG.questions || [];
    if (!pool.length) return;
    /* step === 0 — «нарисуй текущий», а не «следующий»: `step || 1` съедало
       ноль и стартовая отрисовка молча пропускала первый вопрос. */
    qIdx = (qIdx + (step | 0) + pool.length) % pool.length;
    $('ccQ').textContent = pool[qIdx];
}

/* ══ перенос набора ═══════════════════════════════════════════════════ */

function exportKit() {
    const blob = store.exportKit();
    const name = 'timbrica-caller-check.json';
    if (window.__downloadFile) window.__downloadFile(blob, name);
    ae('feature_use', { f: 'cc_kit_export' });
}

async function importKit(file) {
    try {
        const res = store.importKit(await file.text());
        renderPeople();
        renderCallback();
        paintStaticWord();
        paintNowWord();
        paintCircle();
        renderQr();
        paintVoiceReadiness();
        setStatus($('ccKitStatus'), t('kit_imported', { n: res.added }), 'good');
        ae('feature_use', { f: 'cc_kit_import', added: res.added });
    } catch (e) {
        setStatus($('ccKitStatus'), t('kit_bad'), 'bad');
    }
}

/* ══ старт ════════════════════════════════════════════════════════════ */

function bindDrop(id, fn) {
    const el = $(id);
    if (!el) return;
    if (typeof window.__bindUploaderFile === 'function') window.__bindUploaderFile(el, fn);
    else el.addEventListener('tool-uploader:file', (e) => fn(e.detail.file));
}

function boot() {
    if (!window.VoiceVerify) { setTimeout(boot, 120); return; }
    cw.setPool(CFG.words || []);
    const s = store.get();

    /* Ссылка круга приезжает во фрагменте. Стираем её из адреса сразу: во
       вкладке, оставленной открытой, секрет не должен лежать в адресной
       строке, а в истории — быть шарящейся ссылкой. */
    const incoming = cw.circleFromHash(location.hash);
    if (incoming) {
        store.save({ circle: incoming, mode: 'rotating' });
        history.replaceState(null, '', location.pathname + location.search);
        toast(t('circle_joined', { n: incoming.name || t('circle_unnamed') }), 'success');
        ae('feature_use', { f: 'cc_circle_joined' });
    }

    listener = createListener({
        slug: SLUG,
        workletUrls: CFG.workletUrls || [],
        onLevel: (v) => setLevel($('ccLevel'), v),
        onVerdict: paintVerdict,
        onState: (st) => {
            setListenButton(st.running);
            if (st.running) setStatus($('ccStatus'), t('listen_running_' + st.source), 'good');
            else {
                setStatus($('ccStatus'), st.why === 'ended' ? t('listen_ended') : '');
                lastLiveVerdict = null;
            }
        },
    });

    /* Дверь «звук вкладки» рисуется по факту поддержки. */
    const tabBtn = document.querySelector('#ccSrc [data-src="tab"]');
    if (tabBtn && !listener.supportsTab()) tabBtn.remove();

    setTab(s.tab === 'prep' ? 'prep' : 'now');
    on($('ccTabNow'), 'click', () => setTab('now'));
    on($('ccTabPrep'), 'click', () => setTab('prep'));
    on($('ccGoPrep'), 'click', () => { setTab('prep'); focusPrep(); });
    on($('ccGoPrepWord'), 'click', () => { setTab('prep'); const w = $('ccWordStatic'); if (w) w.scrollIntoView({ block: 'nearest' }); });

    renderPeople();
    renderCallback();
    paintStaticWord();
    paintNowWord();
    paintCircle();
    renderQr();
    paintVoiceReadiness();
    nextQuestion(0);
    startWordClock();

    document.querySelectorAll('#ccSrc [data-src]').forEach((b) => {
        b.addEventListener('click', () => setSource(b.dataset.src));
    });
    setSource('mic');
    on($('ccListen'), 'click', toggleListen);
    on($('ccQNext'), 'click', () => { nextQuestion(1); ae('feature_use', { f: 'cc_question_next' }); });
    on($('ccFlagsToggle'), 'click', toggleFlags);

    document.querySelectorAll('[data-wordmode]').forEach((b) => {
        b.addEventListener('click', () => {
            const mode = b.dataset.wordmode;
            store.save({ mode });
            document.querySelectorAll('[data-wordmode]').forEach((x) => x.setAttribute('aria-pressed', String(x.dataset.wordmode === mode)));
            show($('ccWordStaticBox'), mode === 'static');
            show($('ccWordRotBox'), mode === 'rotating');
            paintNowWord();
        });
    });
    const startMode = s.mode === 'rotating' && s.circle ? 'rotating' : 'static';
    const modeBtn = document.querySelector('[data-wordmode="' + startMode + '"]');
    if (modeBtn) modeBtn.click();

    on($('ccGen'), 'click', () => {
        const w = cw.pickStatic();
        if (!w) { setStatus($('ccWordStatus'), t('word_too_common'), 'warn'); return; }
        store.save({ word: w });
        paintStaticWord();
        paintNowWord();
        setStatus($('ccWordStatus'), t('word_generated'), 'good');
        ae('feature_use', { f: 'cc_word_generated' });
    });
    on($('ccCopy'), 'click', async () => {
        const w = store.get().word;
        if (!w) return;
        try { await navigator.clipboard.writeText(w); toast(t('word_copied'), 'success'); } catch (e) {}
        ae('feature_use', { f: 'cc_word_copied' });
    });
    on($('ccPrint'), 'click', () => {
        if (!store.get().word) return;
        ae('feature_use', { f: 'cc_card_printed' });
        window.print();
    });

    on($('ccCircleCreate'), 'click', async () => {
        const c = cw.createCircle(($('ccCircleName').value || '').trim());
        store.save({ circle: c, mode: 'rotating' });
        await paintCircle();
        renderQr();
        paintNowWord();
        ae('feature_use', { f: 'cc_circle_created' });
    });
    on($('ccCircleDrop'), 'click', () => {
        if (!window.confirm(t('circle_confirm_del'))) return;
        store.save({ circle: null, mode: 'static' });
        paintCircle();
        paintNowWord();
        const b = document.querySelector('[data-wordmode="static"]');
        if (b) b.click();
    });
    on($('ccCircleCopy'), 'click', async () => {
        const v = $('ccCircleLink').value;
        try { await navigator.clipboard.writeText(v); toast(t('word_copied'), 'success'); } catch (e) {}
    });

    on($('ccRec'), 'click', recToggle);
    bindDrop('ccEnrollDrop', (file) => {
        const name = ($('ccName').value || '').trim();
        if (!name) { setStatus($('ccRecStatus'), t('enroll_name_needed'), 'warn'); $('ccName').focus(); return; }
        setStatus($('ccRecStatus'), t('analyzing'));
        enroll(file, name, $('ccRecStatus'));
    });

    bindDrop('ccCheckDrop', async (file) => {
        setStatus($('ccStatus'), t('analyzing'));
        show($('ccResult'), false);
        try {
            const r = await listener.analyseFile(file);
            setStatus($('ccStatus'), '');
            if (!r.feat || !r.q.ok) {
                paintVerdict({ verdict: 'unclear', quality: r.q || { ok: false, reason: 'no_speech' }, result: r.feat ? r : null });
                return;
            }
            const people = store.get().people;
            if (!people.length) { paintVerdict({ verdict: 'unclear', noReference: true, result: r }); return; }
            const m = listener.match(r.feat, people, store.unpackFeat);
            const v = window.VoiceVerify.verdict(m.best.cmp, true);
            paintVerdict({ verdict: v, match: m, result: r });
            ae('tool_complete', { verdict: v, score: Math.round(m.best.cmp.score), people: people.length, source: 'file' });
        } catch (e) {
            setStatus($('ccStatus'), t('decode_err'), 'bad');
            ae('tool_error', { stage: 'cc_check_decode' });
        }
    });

    on($('ccExport'), 'click', exportKit);
    on($('ccImportInput'), 'change', (e) => {
        const f = e.target.files && e.target.files[0];
        if (f) importKit(f);
        e.target.value = '';
    });

    initFlags();

    window.addEventListener('pagehide', () => {
        if (listener) listener.stop();
        if (flags) flags.stop();
        recStop();
    });
    try { if (window.__stage) window.__stage('idle'); } catch (e) {}
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();

window.__callerCheck = { store, cw, listener: () => listener };
